import {
  Component,
  OnInit,
  Input,
  AfterViewChecked,
  ChangeDetectorRef,
  NgZone
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import {
  DrawerType,
  IConsultation,
  IConsultationStatus,
  ICustomerUser,
  IQuestionModel
} from '@care-portals/api-interfaces';
import { formStatuses, formTypes, QuestionsCatalog } from '@care-portals/data';
import { ConsultationUtils } from '@care-portals/data';
import { ConsultationFacade } from '../../+store/consultation/consultation.facade';
import { DrawerStateFacade } from '../../+store/drawer-state/drawer-state.facade';
import { APIService } from '../../services/api/api.service';

@Component({
  selector: 'jackhealth-admin-customer-consultation',
  templateUrl: './customer-consultation.component.html',
  styleUrls: ['./customer-consultation.component.less']
})
export class CustomerConsultationComponent implements OnInit, AfterViewChecked {
  @Input() consultation: IConsultation;
  data = {};
  questions = [];
  evaluatedQuestionModels: IQuestionModel[] = [];
  consultationStatuses = {};
  statuses = formStatuses;
  pathwaiToken: string | null = null;
  pathwaiTokenError: string | null = null;

  constructor(
    private drawerRef: NzDrawerRef,
    public facade: ConsultationFacade,
    private cdRef: ChangeDetectorRef,
    private zone: NgZone,
    private drawerFacade: DrawerStateFacade,
    private apiService: APIService
  ) {
    formStatuses.forEach(status => {
      this.consultationStatuses[status.name] = status.label;
    });
  }

  getScore(field: string) {
    const evaluateFunc = QuestionsCatalog.find(
      model => model.type === 'evaluated' && model.field === field
    ).answer;
    return evaluateFunc ? evaluateFunc(this.consultation.answers) : 0;
  }

  get acneUrl() {
    return ConsultationUtils.getAcneUrl(
      this.consultation.customer['firstName'].split(' ')[0]
    );
  }

  get hasPathwaiSubmission(): boolean {
    return !!this.consultation.meta?.pathwai?.submissionId;
  }

  ngOnInit(): void {
    if (formTypes[this.consultation.type]) {
      const mainQuestions = ConsultationUtils.getQuestionStackFromAnswers(
        formTypes[this.consultation.type].questions,
        this.consultation.answers
      );

      const archivedQuestions = (
        formTypes[this.consultation.type].olderVersions ?? []
      )
        .map(version => formTypes[version].questions)
        .map(qs =>
          ConsultationUtils.getQuestionStackFromAnswers(
            qs,
            this.consultation.answers,
            false
          )
        )
        .flat();

      const questions = Array.from(
        new Set([...mainQuestions, ...archivedQuestions])
      );

      this.questions = questions.filter(
        question =>
          typeof question !== 'string' ||
          QuestionsCatalog.find(qModel => qModel.field === question).type !==
            'evaluated'
      );
      this.evaluatedQuestionModels = QuestionsCatalog.filter(
        model => model.type === 'evaluated' && questions.includes(model.field)
      );
    }

    Object.assign(this.data, this.consultation.answers);

    if (this.hasPathwaiSubmission) {
      this.loadPathwaiToken();
    }

    this.cdRef.detectChanges();
  }

  async loadPathwaiToken(): Promise<void> {
    try {
      this.pathwaiToken = null;
      this.pathwaiTokenError = null;
      const result = await firstValueFrom(
        this.apiService.getPathwaiViewerToken(this.consultation._id)
      );
      this.zone.run(() => {
        this.pathwaiToken = result?.data?.token ?? null;
        this.cdRef.detectChanges();
      });
    } catch (error) {
      console.error(
        'CustomerConsultationComponent :: loadPathwaiToken()',
        error
      );
      this.zone.run(() => {
        this.pathwaiTokenError = 'Unable to load Pathwai submission viewer.';
        this.cdRef.detectChanges();
      });
    }
  }

  ngAfterViewChecked(): void {}

  save() {
    this.consultation.answers = this.data;

    this.facade.updateConsultation(this.consultation._id, {
      answers: this.data
    });
  }

  createRx(): void {}

  close(): void {
    this.drawerRef.close();
  }

  closeAndUpdate() {
    this.facade.updateConsultation(
      this.consultation._id,
      { status: 'completed' },
      () => this.drawerRef.close()
    );
  }

  updateStatus(status: IConsultationStatus) {
    this.facade.updateConsultation(
      this.consultation._id,
      { status },
      () => (this.consultation.status = status)
    );
  }

  openCreateConsultationDrawer() {
    this.drawerRef.close();
    this.drawerFacade.openDrawer({
      name: DrawerType.CREATE_CONSULTATION,
      props: {
        consultation: this.consultation,
        customerId:
          (this.consultation.customer as ICustomerUser)._id ||
          (this.consultation.customer as string)
      }
    });
  }
}
